import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Input,
  IconButton,
  Table,
  Button,
} from '@mui/joy';
import {Trash, Pencil, ChevronLeft, ChevronRight, ArrowUp, ArrowDown } from 'lucide-react';
import ModaleUtente from '../../components/ModaleUtente';
import { authFetch } from '../../utils/authFetch';
import { canEditUtenti } from '../../utils/permissions';
import { useNavigate } from 'react-router-dom';

import { toast } from 'react-toastify';
import ModaleConferma from '../../components/ModaleConferma';

export default function Utenti() {
  const [showModale, setShowModale] = useState(false);
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 20; //user per page
  const [sortConfig, setSortConfig] = useState({ key: 'nome', direction: 'asc' });

  const selectedOrg = localStorage.getItem('selectedOrg'); //get id da storage
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const ruoloLabels = {
    0: { label: 'Basic', color: 'neutral' },
    1: { label: 'Visualizzatore', color: 'primary' },
    2: { label: 'Admin', color: 'danger' },
  };

  // Memoizza fetchUsers per non ricrearla ad ogni render
  const fetchUsers = useCallback(async () => {
    if (!selectedOrg) return;
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/user`,
        {
          credentials: 'include',
        }
      );
      if (!res.ok) throw new Error('Errore fetch utenti');
  
      const data = await res.json();
  
      if (Array.isArray(data)) {
        setUsers(data);
      } else {
        console.warn('Risposta inattesa:', data);
        setUsers([]);
        toast.error('Risposta inattesa dal server utenti', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error('Errore nel fetch utenti:', err);
      setUsers([]);
      toast.error('Errore nel caricamento degli utenti', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }, [selectedOrg]);

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
  
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/user/${userToDelete.id}/unlink`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );
  
      if (!res.ok) throw new Error('Errore nella cancellazione');
  
      toast.success('Utente rimosso con successo', { position: 'top-right', autoClose: 3000 });
      fetchUsers();
    } catch (err) {
      console.error(err);
      toast.error('Errore nella rimozione dell’utente', { position: 'top-right', autoClose: 3000 });
    } finally {
      setConfirmDeleteOpen(false);
      setUserToDelete(null);
    }
  };   

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = useMemo(() => {
    const lowerTerm = searchTerm.toLowerCase();
    return users.filter((u) => u.nome.toLowerCase().includes(lowerTerm));
  }, [searchTerm, users]);

  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return filteredUsers;

    return [...filteredUsers].sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];

      if (sortConfig.key === 'id') {
        valA = Number(valA);
        valB = Number(valB);
      } else {
        valA = valA?.toString().toLowerCase() || '';
        valB = valB?.toString().toLowerCase() || '';
      }

      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredUsers, sortConfig]);

  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * perPage;
    return sortedUsers.slice(start, start + perPage);
  }, [page, sortedUsers]);

  const totalPages = Math.ceil(sortedUsers.length / perPage);

  const handleSort = (key) => {
    setPage(1);
    setSortConfig((current) => {
      if (current.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowDown size={14} color="#bbb" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp size={14} color="black" />
    ) : (
      <ArrowDown size={14} color="black" />
    );
  };

  return (

        <Box>
          <Typography level="h3" mb={2}>
            Utenti
          </Typography>

          <Box mb={2} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Input
                placeholder="Cerca utente..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                sx={{ maxWidth: 300 }}
              />
              <Typography level="body-sm" color="neutral">
                {sortedUsers.length} record trovati
              </Typography>
            </Box>
            {canEditUtenti() && (
              <Button
                variant="solid"
                color="primary"
                onClick={() => setShowModale(true)}
              >
                + Aggiungi Utente
              </Button>
            )}
          </Box>

          <Box
            sx={{
              borderRadius: 'lg',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'neutral.outlinedBorder',
            }}
          >
          <Table
            size="sm"
            sx={{
              minWidth: { xs: '100%', sm: 500 },
              borderRadius: 0,
              tableLayout: 'auto',
              '& thead th': {
                bgcolor: 'neutral.softBg',
                textAlign: 'left',
                px: { xs: 1, sm: 2 },
                py: { xs: 0.5, sm: 1 },
                fontSize: { xs: 'xs', sm: 'sm' },
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
              },
              '& tbody td': {
                px: { xs: 1, sm: 2 },
                py: { xs: 0.5, sm: 1 },
                fontSize: { xs: 'sm', sm: 'md' },
                lineHeight: 1.2,
                wordBreak: 'break-word',
              },
              '& tbody tr:hover': {
                bgcolor: 'neutral.plainHoverBg',
              },
            }}
          >
              <thead>
                <tr>
                  <th onClick={() => handleSort('nome')} style={{ cursor: 'pointer' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      Nome
                      <SortIcon columnKey="nome" />
                    </Box>
                  </th>
                  <th onClick={() => handleSort('livello_permesso_massimo')} style={{ cursor: 'pointer' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      Ruolo
                      <SortIcon columnKey="livello_permesso_massimo" />
                    </Box>
                  </th>
                  <th onClick={() => handleSort('username')} style={{ cursor: 'pointer' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      Username
                      <SortIcon columnKey="username" />
                    </Box>
                  </th>
                  {canEditUtenti() && (
                    <th style={{ textAlign: 'center', width: 80 }}>Azioni</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.length > 0 ? (
                  paginatedUsers.map((user) => {
                    const ruolo = ruoloLabels[user.livello_permesso_massimo];
                    return (
                      <tr key={user.id}>
                        <td>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: user.status === 1 ? 'success.solidBg' : 'danger.solidBg',
                              }}
                            />
                            <span>{user.nome}</span>
                          </Box>
                        </td>
                        <td>
                          {ruolo && (
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 'xl',
                                fontSize: 'sm',
                                fontWeight: 'md',
                                bgcolor: `${ruolo.color}.solidBg`,
                                color: '#fff',
                              }}
                            >
                              {ruolo.label}
                            </Box>
                          )}
                        </td>
                        <td>{user.username}</td>
                        {canEditUtenti() && (
                          <td style={{ textAlign: 'center' }}>
                            <Box display="flex" justifyContent="center" gap={1}>
                              <IconButton
                                size="sm"
                                variant="soft"
                                color="primary"
                                onClick={() => navigate(`/utenti/${user.id}`)}
                              >
                                <Pencil size={16} />
                              </IconButton>

                              <IconButton
                                size="sm"
                                variant="soft"
                                color="danger"
                                onClick={() => {
                                  setUserToDelete(user);
                                  setConfirmDeleteOpen(true);
                                }}
                              >
                                <Trash size={16} />
                              </IconButton>
                            </Box>
                          </td>
                        )}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '12px' }}>
                      Nessun utente trovato.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Box>

          {totalPages > 1 && (
            <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
              <Button
                size="sm"
                variant="outlined"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                startDecorator={<ChevronLeft size={14} />}
              >
                Precedente
              </Button>

              <Typography level="body-sm">
                Pagina {page} di {totalPages}
              </Typography>

              <Button
                size="sm"
                variant="outlined"
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                endDecorator={<ChevronRight size={14} />}
              >
                Successiva
              </Button>
            </Box>
          )}

      <ModaleUtente
        open={showModale}
        onClose={() => setShowModale(false)}
        onSuccess={() => {
          fetchUsers();
          setShowModale(false);
          toast.success('Utente aggiunto/modificato con successo', {
            position: 'top-right',
            autoClose: 3000,
          });
        }}
      />
      <ModaleConferma
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Conferma eliminazione"
        message={`Sei sicuro di voler rimuovere ${userToDelete?.nome || 'questo utente'}?`}
      />
    </Box>
  );
}
